import { motion, AnimatePresence } from "framer-motion";
import { X, Radio, ArrowRight, ShieldCheck, TrendingUp, Lightbulb } from "lucide-react";
import { useEffect } from "react";

/**
 * PulseAdvisoryModal — In-app view of a single TaxSetu-branded blog post.
 * Renders the 4-section structure: Headline → What Changed → Impact → Recommendation.
 * NO external links. Copy is 100% in-house TaxSetu voice.
 */

const CAT_CHIP = {
  "Income Tax": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "GST": "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  "Labour Law": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Companies Act": "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  "FEMA/RBI": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  "Capital Markets": "bg-violet-500/15 text-violet-300 border-violet-500/30",
  "Case Laws": "bg-rose-500/15 text-rose-300 border-rose-500/30",
  "Stock Markets": "bg-lime-500/15 text-lime-300 border-lime-500/30",
  "Mixed": "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function PulseAdvisoryModal({ post, isOpen, onClose, onOpenPermalink }) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!post) return null;

  const catCls = CAT_CHIP[post.category] || CAT_CHIP.Mixed;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[110] overflow-y-auto"
          style={{ background: "rgba(2, 6, 23, 0.85)", backdropFilter: "blur(6px)" }}
          data-testid="pulse-advisory-modal"
          onClick={onClose}
        >
          <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6">
            <motion.article
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.22 }}
              className="w-full max-w-2xl rounded-2xl border border-white/10 bg-slate-950 shadow-2xl shadow-black/40 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header strip */}
              <div className="flex items-center justify-between gap-3 px-5 sm:px-7 py-4 border-b border-white/5 bg-gradient-to-r from-rose-950/40 via-slate-950 to-slate-950">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="relative flex h-2 w-2 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
                  </span>
                  <Radio className="w-3.5 h-3.5 text-rose-300 flex-shrink-0" />
                  <span className="text-[10px] font-bold tracking-widest text-rose-300 uppercase whitespace-nowrap">
                    TaxSetu AI · Regulatory Pulse
                  </span>
                </div>
                <button
                  onClick={onClose}
                  className="text-slate-400 hover:text-white p-1.5 rounded-md hover:bg-white/5 flex-shrink-0"
                  aria-label="Close"
                  data-testid="pulse-advisory-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="px-5 sm:px-7 py-5 sm:py-6">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className={`inline-block text-[9.5px] font-bold uppercase tracking-widest border rounded-full px-2 py-0.5 ${catCls}`}>
                    {post.category || "Mixed"}
                  </span>
                  {post.status && (
                    <span className="inline-block text-[9.5px] font-bold uppercase tracking-widest border rounded-full px-2 py-0.5 bg-white/[0.03] border-white/15 text-slate-200">
                      Status: {post.status}
                    </span>
                  )}
                  <span className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">
                    · {formatDate(post.published_at)}
                  </span>
                </div>

                <h2
                  className="text-xl sm:text-2xl font-heading font-bold text-white leading-tight mb-3"
                  data-testid="pulse-advisory-title"
                >
                  {post.title}
                </h2>

                {post.notification_ref && (
                  <div className="mb-4 rounded-lg border border-slate-700/60 bg-slate-900/60 px-3 py-2 text-[11.5px] text-slate-300">
                    <span className="font-semibold text-slate-200">Official reference:</span>{" "}
                    {post.notification_ref}
                  </div>
                )}

                {/* What Changed */}
                <section className="mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                      <ShieldCheck className="w-3.5 h-3.5 text-cyan-300" />
                    </div>
                    <h3 className="text-[11px] font-bold tracking-widest text-cyan-300 uppercase">
                      What Changed
                    </h3>
                  </div>
                  <p className="text-[13.5px] sm:text-sm text-slate-200 leading-relaxed">
                    {post.what_changed}
                  </p>
                </section>

                {/* Impact */}
                <section className="mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                      <TrendingUp className="w-3.5 h-3.5 text-amber-300" />
                    </div>
                    <h3 className="text-[11px] font-bold tracking-widest text-amber-300 uppercase">
                      Impact on Taxpayers & Practitioners
                    </h3>
                  </div>
                  <p className="text-[13.5px] sm:text-sm text-slate-200 leading-relaxed">
                    {post.impact}
                  </p>
                </section>

                {/* Recommendation */}
                <section className="mb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                      <Lightbulb className="w-3.5 h-3.5 text-emerald-300" />
                    </div>
                    <h3 className="text-[11px] font-bold tracking-widest text-emerald-300 uppercase">
                      TaxSetu AI Recommendation
                    </h3>
                  </div>
                  <p className="text-[13.5px] sm:text-sm text-slate-200 leading-relaxed">
                    {post.recommendation}
                  </p>
                </section>
              </div>

              {/* Footer */}
              <div className="px-5 sm:px-7 py-4 border-t border-white/5 bg-white/[0.02] flex items-center justify-between gap-3">
                <p className="text-[10.5px] text-slate-500 leading-snug">
                  Informational blog post by TaxSetu AI. Please verify against the applicable law before acting.
                </p>
                {post.slug && (
                  <button
                    onClick={() => onOpenPermalink?.(post.slug)}
                    className="flex items-center gap-1.5 text-[11.5px] font-semibold text-cyan-300 hover:text-cyan-200 whitespace-nowrap"
                    data-testid="pulse-advisory-permalink"
                  >
                    Open full page
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </motion.article>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
